import { CombinedDownload } from '@goldcast/api/content';
import { IconCloudDownload, IconDownload, IconSettings } from '@tabler/icons-react';
import { ReactNode, useCallback, useRef, useState, useEffect } from 'react';
import DownloadsPopupListItem from './DownloadsPopupListItem';
import BrandingToggle from '../SharePopup/BrandingToggle';
import Loader from '@/components/atoms/Loader';
import Button from '@/components/atoms/Button/Button';

interface DownloadsPopupV2LayoutProps {
  loading: boolean;
  processingDownloads: CombinedDownload[];
  recentDownloads: CombinedDownload[];
  downloadAction: () => Promise<void>;
  statusMessage: string | null;
  isDownloadDisabled: boolean;
  languageDropdown?: ReactNode;
  showBrandingToggle?: boolean;
}

export default function DownloadsPopupV2Layout({
  loading,
  processingDownloads,
  recentDownloads,
  downloadAction,
  statusMessage,
  isDownloadDisabled,
  languageDropdown,
  showBrandingToggle
}: Readonly<DownloadsPopupV2LayoutProps>) {
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const handleSettingsToggle = useCallback(() => {
    setIsSettingsOpen(prev => !prev);
  }, []);
  const settingsDropdownRef = useRef<HTMLDivElement>(null);
  const hasAnyDownloads = processingDownloads.length > 0 || recentDownloads.length > 0;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (settingsDropdownRef.current && !settingsDropdownRef.current.contains(event.target as Node)) {
        setIsSettingsOpen(false);
      }
    };

    if (isSettingsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isSettingsOpen]);

  if (loading) {
    return (
      <div className="absolute right-0 w-[28rem] translate-y-2 rounded-xl border bg-white shadow-lg">
        <div className="flex h-56 items-center justify-center">
          <Loader />
        </div>
      </div>
    );
  }

  return (
    <div className="absolute right-0 w-[28rem] translate-y-2 rounded-xl border bg-white shadow-lg">
      <div className="relative px-4 py-3">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold">Download</h3>
          <div className="flex items-center gap-2">
            {showBrandingToggle && (
              <button
                onClick={handleSettingsToggle}
                className="flex size-9 items-center justify-center rounded-lg hover:bg-gray-100"
                title="Download settings"
              >
                <IconSettings className="size-5 text-gray-600" />
              </button>
            )}

            <Button
              onClick={downloadAction}
              disabled={isDownloadDisabled}
              variation="filled"
              trackingId="new-download-button"
              buttonSize="base"
              className="h-10 w-[155px] gap-2 rounded-lg !bg-slate-950 !text-white hover:!bg-deep-orange disabled:!bg-gray-300 disabled:!text-gray-500"
            >
              <IconDownload className="size-4" />
              New Download
            </Button>
          </div>
        </div>

        {statusMessage && <div className="mt-2 text-xs text-gray-600">{statusMessage}</div>}

        {isSettingsOpen && showBrandingToggle && (
          <div
            ref={settingsDropdownRef}
            className="absolute right-4 top-full z-10 mt-1 w-80 rounded-md border bg-white p-4 shadow-lg"
          >
            <div className="grid grid-cols-3 items-center gap-y-2.5 text-sm">
              <BrandingToggle />
            </div>
          </div>
        )}

        {languageDropdown}
      </div>

      <div className="max-h-96 overflow-y-auto overflow-x-hidden">
        {hasAnyDownloads ? (
          <>
            {processingDownloads.length > 0 && (
              <div className="pt-2">
                {processingDownloads.map(download => (
                  <DownloadsPopupListItem key={`${download.id}_${download.status}`} download={download} />
                ))}
              </div>
            )}

            {recentDownloads.length > 0 && (
              <div className="pb-2">
                <div className="px-4 pb-2 pt-3 text-sm font-medium text-slate-600">Previous downloads</div>
                {recentDownloads.map(download => (
                  <DownloadsPopupListItem key={`${download.id}_${download.status}`} download={download} />
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="flex h-56 flex-col items-center justify-center">
            <IconCloudDownload className="size-6 text-slate-400" />
            <div className="mt-2 flex flex-col items-center justify-center">
              <div className="text-sm font-medium">No downloads, yet.</div>
              <div className="mt-1 max-w-48 text-center text-sm text-slate-600">
                Click on "New download" to start downloading your asset.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
